import { useEffect, useState } from "react";
import { PriorityBadge } from "../PriorityBadge";
import { CategoryBadge } from "../CategoryBadge";
import AuditTrailButton from "../AuditTrailButton";
import {
  Mail,
  ChevronDown,
  ChevronRight,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export interface DraftCardProps {
  email_id: string;
  thread_id: string;
  subject: string | null;
  body: string;
  category: string | null;
  priority: string | null;
  from_email: string;
  draft_body: string;
  received_at: string;
  onApprove: (emailId: string) => void;
  onEdit: (emailId: string, body: string) => void | Promise<unknown>;
  pending?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onReject: (emailId: string) => void;
}

export function DraftCard({
  email_id,
  subject,
  body,
  category,
  priority,
  from_email,
  draft_body,
  received_at,
  onApprove,
  onEdit,
  onReject,
  pending = false,
  onDirtyChange,
}: DraftCardProps) {
  const [isOriginalExpanded, setIsOriginalExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDraft, setEditedDraft] = useState(draft_body);

  const [saving, setSaving] = useState(false);
  const dirty = isEditing && editedDraft !== draft_body;
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const handleApprove = () => {
    if (
      window.confirm("Sei sicuro di voler approvare e inviare questo draft?")
    ) {
      onApprove(email_id);
    }
  };

  const handleReject = () => {
    if (
      window.confirm(
        "Sei sicuro di voler rifiutare questo draft? L'email tornerà in coda per la gestione manuale.",
      )
    ) {
      onReject(email_id);
    }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await onEdit(email_id, editedDraft);
      setIsEditing(false);
    } catch {
      /* parent reports the failure; keep edited text */
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditedDraft(draft_body);
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card className="mail-compose overflow-hidden">
      {/* Header */}
      <CardHeader className="bg-muted/50 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-0.5 truncate">
              {subject || "Senza oggetto"}
            </h3>
            <p className="text-xs text-muted-foreground">
              Da: <span className="font-medium">{from_email}</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Ricevuta: {formatDate(received_at)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <AuditTrailButton
              emailId={email_id}
              size="icon"
              variant="ghost"
              className="size-7"
            />
            <PriorityBadge priority={priority} />
            <CategoryBadge category={category} />
          </div>
        </div>
      </CardHeader>

      {/* Email Originale */}
      <div className="border-b border-border">
        <button
          onClick={() => setIsOriginalExpanded(!isOriginalExpanded)}
          className="w-full px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors flex items-center justify-between"
        >
          <span className="flex items-center gap-1.5">
            <Mail className="size-3.5" />
            Email Originale
          </span>
          {isOriginalExpanded ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
        </button>
        {isOriginalExpanded && (
          <div className="p-4 bg-background">
            <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-sans">
              {body}
            </pre>
          </div>
        )}
      </div>

      {/* Draft Section */}
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Risposta da approvare
          </h4>
          {!isEditing && (
            <Button
              variant="link"
              className="h-auto p-0 text-xs text-primary"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="size-3 mr-1" />
              Modifica
            </Button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editedDraft}
              onChange={(e) => setEditedDraft(e.target.value)}
              className="min-h-64"
              aria-label="Corpo della risposta"
              disabled={saving || pending}
              placeholder="Scrivi qui la risposta..."
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleEditSave}
                disabled={pending || saving}
              >
                Salva
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditCancel}
                disabled={pending || saving}
              >
                Annulla
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-muted/30 rounded-lg border border-border">
            <pre className="text-sm text-foreground/80 whitespace-pre-wrap font-sans">
              {draft_body}
            </pre>
          </div>
        )}
      </CardContent>

      {/* Action Buttons */}
      <div className="px-4 pb-4 pt-2 flex flex-wrap justify-end gap-2">
        <Button
          onClick={handleApprove}
          disabled={isEditing || pending || saving}
          className="order-last"
        >
          <Check className="size-4" />
          Approva e Invia
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsEditing(true)}
          disabled={isEditing || pending || saving}
          className=""
        >
          <Pencil className="size-4" />
          Modifica
        </Button>
        <Button
          variant="outline"
          onClick={handleReject}
          disabled={isEditing || pending || saving}
          className=""
        >
          <X className="size-4" />
          Rifiuta
        </Button>
      </div>
    </Card>
  );
}
